import { useEffect, useMemo, useRef, useState } from 'react'
import {
  CalendarDays, Table2, ChevronLeft, ChevronRight, Loader2, Car, MapPin, Clock,
} from 'lucide-react'
import { apiGet, cachedGet } from '../../lib/api.js'
import { usePersistedState } from '../../lib/persist.js'
import { nowYM, stepMonth, fmtMonthTH, monthRange, LEAVE_PERSONAL, LEAVE_SICK } from '../../lib/timetable.js'
import { leaveQuota } from '../../lib/leave.js'
import {
  shiftTypeTone, relabelBranch, isLeaveRow, otMode,
  fmtTime, fmtDate, travelFee, clockinTs, clockoutTs, shiftStart, shiftEnd,
} from '../../lib/clockin.js'
import { monthDays, DOW_TH, shortType } from '../../lib/employeeMatrix.js'
import { bkkToday, readableError } from '../../lib/meClockin.js'

// พื้นที่ของฉัน → ตารางเวร. The staff member's OWN roster, one month at a time.
//
// Reads `/api/me/clockin`, which is not a misnomer worth a second endpoint: that
// proxy is "my employee_timetable rows for a date range", forced to the session's
// own employee_id, and a shift and its punch are THE SAME ROW. Duplicating it
// under a second name would give two things to keep in step for no gain.
//
// Read-only on purpose. Who works when is the roster's decision — a staff member
// asking for a change asks a human, and a button here would imply otherwise.
//
// Two views because they answer different questions. **Calendar is the default**:
// the roster question people actually have is "am I working Saturday?", and a
// month grid answers it at a glance where a list makes you scan. The table is
// for the other question — "how many shifts / how much travel this month" — and
// for anyone who wants to read the details in one pass.

const VIEWS = [
  { key: 'calendar', label: 'ปฏิทิน', icon: CalendarDays },
  { key: 'table',    label: 'ตาราง',  icon: Table2 },
]

export default function MeSchedule() {
  const [ym, setYm] = useState(() => nowYM())
  const [view, setView] = usePersistedState('me:sched:view', 'calendar')
  const [rows, setRows] = useState([])
  const [branches, setBranches] = useState({})
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [picked, setPicked] = useState('')   // selected day, 'YYYY-MM-DD'
  const [yearLeave, setYearLeave] = useState(null)  // leave rows for the whole year
  const [profile, setProfile] = useState(null)      // for leaveQuota(); null = not loaded
  const reqRef = useRef(0)
  const yearRef = useRef(0)

  const today = bkkToday()

  useEffect(() => {
    cachedGet('/api/branches')
      .then((r) => {
        const list = Array.isArray(r) ? r : (r?.items || [])
        setBranches(Object.fromEntries(list.map((b) => [String(b.id), b])))
      })
      .catch(() => { /* names degrade to '—'; the roster still reads */ })
  }, [])

  const range = useMemo(() => monthRange(ym.year, ym.month), [ym])

  /* วันลา is counted over two windows because they answer different questions:
   * the month is "what does this month look like", the YEAR is the one that
   * matters — leave entitlement (leaveQuota(), lib/leave.js) is annual, so a monthly
   * figure alone can never tell someone how much they have left.
   *
   * Fetched with `type=leave` so Xano returns only ลากิจ/ลาป่วย. A whole year of
   * shifts would be ~300 rows, each carrying three aggregate addons; a year of
   * leave is a dozen. */
  useEffect(() => {
    const seq = ++yearRef.current
    apiGet('/api/me/clockin', {
      start_date: `${ym.year}-01-01`, end_date: `${ym.year}-12-31`, type: 'leave', per_page: 60,
    })
      .then((r) => { if (seq === yearRef.current) setYearLeave(Array.isArray(r?.items) ? r.items : []) })
      .catch(() => { if (seq === yearRef.current) setYearLeave(null) })
  }, [ym.year])

  useEffect(() => {
    apiGet('/api/me/profile')
      .then((p) => setProfile(p || null))
      .catch(() => { /* no quota → show the count alone, never a wrong denominator */ })
  }, [])

  useEffect(() => {
    const seq = ++reqRef.current
    setLoading(true)
    setErr('')
    setPicked('')
    apiGet('/api/me/clockin', { ...range, per_page: 60 })
      .then((r) => { if (seq === reqRef.current) setRows(Array.isArray(r?.items) ? r.items : []) })
      .catch((e) => { if (seq === reqRef.current) setErr(readableError(e, 'โหลดตารางเวรไม่สำเร็จ')) })
      .finally(() => { if (seq === reqRef.current) setLoading(false) })
  }, [range.start_date, range.end_date])

  // A day can hold more than one row (a shift plus a leave request, a split
  // day), so this is date → LIST. Keying to a single row would silently hide
  // the second one, and "I thought I was off" is exactly the failure this page
  // exists to prevent.
  const byDate = useMemo(() => {
    const m = new Map()
    for (const r of rows) {
      const d = String(r.date || '').slice(0, 10)
      if (!d) continue
      if (!m.has(d)) m.set(d, [])
      m.get(d).push(r)
    }
    return m
  }, [rows])

  const days = useMemo(() => monthDays(ym.year, ym.month), [ym])

  const totals = useMemo(() => {
    const shifts = rows.filter((r) => !isLeaveRow(r))
    return {
      shifts: shifts.length,
      leave: rows.length - shifts.length,
      travel: rows.reduce((n, r) => n + travelFee(r), 0),
    }
  }, [rows])

  // ⚠️ The SAME leaveQuota() as ขอลา (MeLeave) and HR's การลา tab — NOT
  // Employee.leavequota, an older, different answer. Two tabs of พื้นที่ของฉัน
  // showing two different quotas is how a quota argument starts. Counted the way
  // MeLeave counts (inactive rows skipped, ลากิจ and ลาป่วย apart), and measured on
  // ลากิจ, the quota anyone actually runs out of — ลาป่วย is a fixed 30.
  const yearQuota = useMemo(() => {
    if (yearLeave == null) return null
    let personal = 0
    let sick = 0
    for (const r of yearLeave) {
      if (r?.active === false) continue
      if (r?.type === LEAVE_SICK) sick += 1
      else if (r?.type === LEAVE_PERSONAL) personal += 1
    }
    return leaveQuota(profile, ym.year, { personal, sick })
  }, [yearLeave, profile, ym.year])

  const atCurrent = ym.year === nowYM().year && ym.month === nowYM().month

  return (
    <div className="max-w-[720px]">
      {/* Month stepper + view switch */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-2 mb-4">
        <div className="flex items-center gap-1">
          <StepBtn onClick={() => setYm((v) => stepMonth(v, -1))} title="เดือนก่อนหน้า">
            <ChevronLeft size={16} strokeWidth={2} />
          </StepBtn>
          <span className="text-[15px] font-semibold text-slate-900 whitespace-nowrap min-w-[124px] text-center">
            {fmtMonthTH(ym.year, ym.month)}
          </span>
          <StepBtn onClick={() => setYm((v) => stepMonth(v, 1))} title="เดือนถัดไป">
            <ChevronRight size={16} strokeWidth={2} />
          </StepBtn>
          {!atCurrent && (
            <button
              type="button"
              onClick={() => setYm(nowYM())}
              className="ml-1 text-[12px] text-brand-600 hover:text-brand-700 whitespace-nowrap"
            >
              เดือนนี้
            </button>
          )}
        </div>

        <div className="ml-auto flex items-center gap-0.5 rounded-xl bg-slate-100 p-0.5">
          {VIEWS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setView(key)}
              className={`inline-flex items-center gap-1.5 rounded-[10px] px-2.5 py-1.5 text-[12.5px] font-medium transition ${
                view === key ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <Icon size={13} strokeWidth={1.9} />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* The numbers people check. วันลา appears twice on purpose — the month is
          context, the YEAR is the one measured against an annual entitlement. */}
      {/* ลากิจ and ลาป่วย are two separate quotas (leaveQuota), so two tiles — the
          same two figures ขอลา shows (Ram, 2026-10-18). Year in ค.ศ. (screen rule). */}
      <div className="grid grid-cols-2 sm:grid-cols-5 divide-x divide-y sm:divide-y-0 divide-slate-100 rounded-2xl border border-slate-200/80 bg-white mb-4 overflow-hidden">
        <Stat label="เวรเดือนนี้" value={totals.shifts} />
        <Stat label="ลาเดือนนี้" value={totals.leave} />
        <Stat
          label={`ลากิจปี ${ym.year}`}
          value={yearQuota == null ? '—' : yearQuota.personal.used}
          sub={/* no profile → the count alone, never a wrong denominator */ yearQuota && profile ? `จาก ${yearQuota.personal.quota} วัน` : ''}
          tone={yearQuota && profile && yearQuota.personal.over ? 'text-rose-600' : ''}
        />
        <Stat
          label={`ลาป่วยปี ${ym.year}`}
          value={yearQuota == null ? '—' : yearQuota.sick.used}
          sub={yearQuota && profile ? `จาก ${yearQuota.sick.quota} วัน` : ''}
          tone={yearQuota && profile && yearQuota.sick.over ? 'text-rose-600' : ''}
        />
        <Stat label="ค่าเดินทาง" value={totals.travel ? totals.travel.toLocaleString('en-US') : '—'} />
      </div>

      {err && <p className="text-[13px] text-rose-500 mb-3">{err}</p>}

      {loading && (
        <div className="flex items-center gap-2 text-[13.5px] text-slate-400 py-12 justify-center">
          <Loader2 size={15} className="animate-spin" /> กำลังโหลด…
        </div>
      )}

      {!loading && !err && view === 'calendar' && (
        <>
          <MonthGrid
            days={days}
            byDate={byDate}
            branches={branches}
            today={today}
            picked={picked}
            onPick={setPicked}
          />
          <DayDetail rows={byDate.get(picked) || []} date={picked} branches={branches} />
        </>
      )}

      {!loading && !err && view === 'table' && (
        <ShiftTable days={days} byDate={byDate} branches={branches} today={today} />
      )}
    </div>
  )
}

/* ── calendar ────────────────────────────────────────────────────────────── */

function MonthGrid({ days, byDate, branches, today, picked, onPick }) {
  // Blanks before the 1st so the month lines up under its weekday columns.
  const lead = days.length ? days[0].dow : 0

  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white overflow-hidden">
      <div className="grid grid-cols-7 border-b border-slate-100">
        {DOW_TH.map((d, i) => (
          <div
            key={d}
            className={`px-1 py-2 text-center text-[11px] font-medium ${
              i === 0 || i === 6 ? 'text-slate-400' : 'text-slate-500'
            }`}
          >
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {Array.from({ length: lead }, (_, i) => (
          <div key={`lead-${i}`} className="min-h-[62px] sm:min-h-[76px] border-b border-r border-slate-50" />
        ))}

        {days.map((d) => {
          const list = byDate.get(d.date) || []
          const isToday = d.date === today
          const isPicked = d.date === picked
          return (
            <button
              key={d.date}
              type="button"
              onClick={() => onPick(isPicked ? '' : d.date)}
              // Every day is a cell, including empty ones — a blank Saturday IS
              // the answer to "am I working this weekend", and a grid that only
              // draws what exists cannot show it.
              className={`min-h-[62px] sm:min-h-[76px] border-b border-r border-slate-50 p-1 text-left align-top transition ${
                d.weekend ? 'bg-slate-50/50' : ''
              } ${isPicked ? 'ring-2 ring-inset ring-brand-400' : 'hover:bg-slate-50'}`}
            >
              <span
                className={`inline-flex items-center justify-center w-[20px] h-[20px] rounded-full text-[11.5px] tabular-nums ${
                  isToday ? 'bg-brand-600 text-white font-semibold' : 'text-slate-500'
                }`}
              >
                {d.day}
              </span>
              <span className="block mt-0.5 space-y-0.5">
                {list.map((r) => (
                  <span
                    key={r.id}
                    title={`${r.type} · ${relabelBranch(branches[String(r.branch_id)]?.name)}`}
                    className={`block truncate rounded-md px-1 py-[1px] text-[10.5px] leading-tight ring-1 ${shiftTypeTone(r.type)}`}
                  >
                    {/* Full type where the cell can hold it. Below `sm` a column
                        is ~48px and "เวรผู้ช่วย" would truncate to "เวรผู้…",
                        which reads as nothing — so the phone keeps the
                        deliberate two-character short form the HR matrix uses. */}
                    <span className="hidden sm:inline">{r.type}</span>
                    <span className="sm:hidden">{shortType(r.type)}</span>
                  </span>
                ))}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

// The tapped day, spelled out. The grid can only fit a two-character type, so
// this is where branch, hours and travel fee actually live on a phone.
function DayDetail({ rows, date, branches }) {
  if (!date) {
    return (
      <p className="text-[12px] text-slate-400 text-center mt-3">แตะวันที่เพื่อดูรายละเอียด</p>
    )
  }
  if (!rows.length) {
    return (
      <div className="mt-3 rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 px-4 py-5 text-center">
        <p className="text-[13px] text-slate-500">{fmtDate(date)} — ไม่มีเวร</p>
      </div>
    )
  }
  return (
    <div className="mt-3 space-y-2">
      {rows.map((r) => <ShiftDetailCard key={r.id} row={r} branches={branches} />)}
    </div>
  )
}

function ShiftDetailCard({ row, branches }) {
  const bname = relabelBranch(branches[String(row.branch_id)]?.name)
  const mode = otMode(row)
  const fee = travelFee(row)
  const punchedIn = clockinTs(row)
  const punchedOut = clockoutTs(row)
  const leave = isLeaveRow(row)

  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white px-4 py-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[12px] px-2 py-[2px] rounded-full ring-1 ${shiftTypeTone(row.type)}`}>
          {row.type}
        </span>
        <span className="text-[13px] font-medium text-slate-800">{fmtDate(row.date)}</span>
        {mode !== 'none' && (
          <span className={`text-[10.5px] px-1.5 py-[1px] rounded-full ring-1 ${
            mode === 'special'
              ? 'bg-indigo-50 text-indigo-700 ring-indigo-200'
              : 'bg-emerald-50 text-emerald-700 ring-emerald-200'
          }`}>
            {mode === 'special' ? 'OT พิเศษ' : 'อนุมัติ OT'}
          </span>
        )}
      </div>

      {!leave && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-[12.5px] text-slate-600">
          <span className="inline-flex items-center gap-1.5">
            <MapPin size={12.5} strokeWidth={1.9} className="text-slate-400" />
            {bname}
          </span>
          {shiftStart(row) && (
            <span className="inline-flex items-center gap-1.5 tabular-nums">
              <Clock size={12.5} strokeWidth={1.9} className="text-slate-400" />
              {fmtTime(shiftStart(row))} – {fmtTime(shiftEnd(row))}
            </span>
          )}
          {fee > 0 && (
            <span className="inline-flex items-center gap-1.5 tabular-nums">
              <Car size={12.5} strokeWidth={1.9} className="text-slate-400" />
              {fee.toLocaleString('en-US')}
            </span>
          )}
        </div>
      )}

      {/* The punch, if there is one — read-only. Recording it is ลงเวลา's job,
          but hiding it here would mean two screens disagreeing about one row. */}
      {!leave && (punchedIn || punchedOut) && (
        <p className="text-[12px] text-slate-400 mt-1.5 tabular-nums">
          ลงเวลา {punchedIn ? fmtTime(punchedIn) : '--:--'} – {punchedOut ? fmtTime(punchedOut) : '--:--'}
        </p>
      )}
    </div>
  )
}

/* ── table ───────────────────────────────────────────────────────────────
 * Only the days that HAVE something. The calendar already answers "which days
 * am I off" by showing them blank; repeating all 31 rows here would bury the
 * twenty that matter. */

function ShiftTable({ days, byDate, branches, today }) {
  const listed = days.flatMap((d) => (byDate.get(d.date) || []).map((r) => ({ d, r })))

  if (!listed.length) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 px-6 py-10 text-center">
        <p className="text-[13.5px] text-slate-500">เดือนนี้ยังไม่มีเวรในตาราง</p>
      </div>
    )
  }

  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white px-4 sm:px-5 py-3">
      <table className="w-full border-collapse text-[12.5px]">
        <thead>
          <tr className="text-[11px] font-medium text-slate-400 border-b border-slate-200/70">
            <th className="font-medium text-left pb-2 pl-0 pr-2">วันที่</th>
            <th className="font-medium text-left pb-2 px-2">ประเภท</th>
            <th className="font-medium text-left pb-2 px-2">สาขา</th>
            <th className="font-medium text-left pb-2 px-2">เวลา</th>
            <th className="font-medium text-right pb-2 px-2">เดินทาง</th>
            <th className="font-medium text-right pb-2 pl-2 pr-0">OT</th>
          </tr>
        </thead>
        <tbody>
          {listed.map(({ d, r }) => {
            const mode = otMode(r)
            const fee = travelFee(r)
            const leave = isLeaveRow(r)
            const bname = relabelBranch(branches[String(r.branch_id)]?.name)
            return (
              <tr key={r.id} className={`border-b border-slate-50 last:border-0 ${d.date === today ? 'bg-brand-50/40' : ''}`}>
                {/* dd/mm/yyyy ค.ศ. — fmtDate(), the same form the ลงเวลา report
                    uses. Roster and payroll get read against each other, and a
                    Buddhist year here would not line up with either. */}
                <td className="py-2.5 pl-0 pr-2 text-slate-700 tabular-nums whitespace-nowrap">
                  {fmtDate(d.date)}
                  <span className="text-slate-400 ml-1.5">{DOW_TH[d.dow]}</span>
                </td>
                <td className="py-2.5 px-2">
                  <span className={`text-[11px] px-1.5 py-[1px] rounded-full ring-1 whitespace-nowrap ${shiftTypeTone(r.type)}`}>
                    {r.type}
                  </span>
                </td>
                <td className="py-2.5 px-2 text-slate-500">
                  <span className="block max-w-[110px] truncate" title={bname}>{leave ? '—' : bname}</span>
                </td>
                <td className="py-2.5 px-2 text-slate-600 tabular-nums whitespace-nowrap">
                  {leave || !shiftStart(r)
                    ? <span className="text-slate-300">—</span>
                    : `${fmtTime(shiftStart(r))} – ${fmtTime(shiftEnd(r))}`}
                </td>
                <td className={`py-2.5 px-2 text-right tabular-nums ${fee > 0 ? 'text-slate-600' : 'text-slate-300'}`}>
                  {fee > 0 ? fee.toLocaleString('en-US') : '—'}
                </td>
                <td className="py-2.5 pl-2 pr-0 text-right">
                  {mode === 'none'
                    ? <span className="text-slate-300">—</span>
                    : (
                      <span className={`text-[10.5px] px-1.5 py-[1px] rounded-full ring-1 whitespace-nowrap ${
                        mode === 'special'
                          ? 'bg-indigo-50 text-indigo-700 ring-indigo-200'
                          : 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                      }`}>
                        {mode === 'special' ? 'OT พิเศษ' : 'อนุมัติ OT'}
                      </span>
                    )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/* ── bits ────────────────────────────────────────────────────────────────── */

function StepBtn({ children, ...rest }) {
  return (
    <button
      type="button"
      {...rest}
      className="w-8 h-8 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center disabled:opacity-30"
    >
      {children}
    </button>
  )
}

function Stat({ label, value, sub, tone = '' }) {
  return (
    <div className="px-4 py-3">
      <div className="text-[11.5px] text-slate-400 whitespace-nowrap">{label}</div>
      <div className="flex items-baseline gap-1.5">
        <span className={`text-[17px] font-semibold tabular-nums ${tone || 'text-slate-800'}`}>{value}</span>
        {sub && <span className="text-[11px] text-slate-400 whitespace-nowrap">{sub}</span>}
      </div>
    </div>
  )
}
